import * as React from 'react'
import { FC } from 'react'
import { Controller } from 'react-hook-form'
import ReCAPTCHA from 'react-google-recaptcha'
import { usePromiseTracker } from 'react-promise-tracker'
import Loader from 'react-promise-loader'
import Button from '../Button/Button'
import { cls } from '../../utils/utils'
import { CAPTCHA_REQUIRED } from '../../utils/constants'
import ModalComponent from '../Modal/Modal'
import CheckBox from '../CheckBox/CheckBox'
import SvgIcon from '../SVGIcon/SvgIcon'
import ShyHyphens from '../ShyHyphens/ShyHyphens'
import EventRegistration from './dialogs/EventRegistration/EventRegistration'
import { IDialog } from './Dialog.types'
import styles from './Dialog.module.scss'
import { FORM_CONSTRAINTS, mailMessages } from './constants'
import { useDialog } from './Dialog.api'

const Dialog: FC<IDialog> = (props) => {
  const {
    messageToShow,
    register,
    formState,
    getFieldState,
    closeModal,
    openModal,
    setIsOpen,
    closeThankYouModal,
    thankYouModalIsOpen,
    modalIsOpen,
    onSubmit,
    setValue,
    form,
    control,
    recaptchaRef,
    captchaKey,
  } = useDialog(props)

  const { promiseInProgress } = usePromiseTracker()
  const isModalBig = props.dialogType === 'CourseRegistration' || props.dialogType === 'EventRegistration'

  const showMessage = () => {
    let messageStyle: string
    if (messageToShow) {
      messageStyle = messageToShow === mailMessages.success ? styles.resultSuccess : styles.resultFail
    } else {
      return null
    }

    return <div className={`${styles.result} ${messageStyle} text--large`}>{messageToShow}</div>
  }

  const dialogTitle = props.dialogTitle || props.buttonText

  const renderDialog = () => {
    switch (props.dialogType) {
      case 'EventRegistration': {
        return (
          <EventRegistration
            event={props.event?.title || ''}
            register={register}
            errors={formState.errors}
            control={control}
            getFieldState={getFieldState}
            formState={formState}
          />
        )
      }
      default: {
        return null
      }
    }
  }

  return (
    <>
      <ModalComponent
        overlayClassName={styles.overlay}
        contentContainerClassName={styles.contentContainer}
        className={cls(styles.modal, isModalBig && styles.bigModal)}
        closeModal={closeModal}
        modalIsOpen={modalIsOpen}
        hasMobileHeader
        hasFooter
      >
        <Loader loading={promiseInProgress} />
        <form id="dialogForm" ref={form} onSubmit={onSubmit} noValidate className={styles.form}>
          <div className={styles.contentWrapper}>
            <h1 className={styles.title}>
              <ShyHyphens text={dialogTitle} />
            </h1>
            <div className={styles.inputsWrapper}>
              {renderDialog()}

              <div className={styles.assignContainer}>
                <CheckBox errors={formState.errors} register={register} />

                <div className={styles.bottomItems}>
                  <div className={styles.captchaWrapper}>
                    <div className={styles.captchaBlock}>
                      <Controller
                        rules={{ required: CAPTCHA_REQUIRED }}
                        name="recaptcha"
                        defaultValue={''}
                        render={() => (
                          <ReCAPTCHA
                            key={captchaKey}
                            onChange={(token) => setValue('recaptcha', token ? token : '')}
                            ref={recaptchaRef}
                            sitekey={FORM_CONSTRAINTS.recaptcha.key}
                            theme="light"
                            size="normal"
                          />
                        )}
                        control={control}
                      />
                      {formState.errors.recaptcha && (
                        <p className={cls('text--button', styles.captchaError)}>Please, complete the captcha</p>
                      )}
                    </div>
                    {showMessage()}
                  </div>

                  <div className={styles.buttonContainer}>
                    <Button
                      type="button"
                      onClick={closeModal}
                      isClear={true}
                      className={cls(styles.button, styles.cancelButton, styles.buttonWithLowercase)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className={cls(styles.button, styles.buttonWithLowercase)} disabled={formState.isSubmitting}>
                      Submit
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </ModalComponent>
      <a className={props.className} onClick={() => openModal(setIsOpen)}>
        {props.noButton ? <span className={props.textClassname}>{props.buttonText}</span> : <Button>{props.buttonText}</Button>}
      </a>
      <ModalComponent
        overlayClassName={styles.overlay}
        className={cls(styles.modal, styles.thankYouModal)}
        closeModal={closeThankYouModal}
        modalIsOpen={thankYouModalIsOpen}
      >
        <div className={styles.thankYouContainer}>
          <SvgIcon href="/images/sun.svg#sun" className={styles.thankYouIcon} />
          <h2 className={styles.thankYou}>{props.dialogType !== 'EventFeedback' ? 'Thank you!' : 'Thank you for your opinion!'}</h2>
        </div>
      </ModalComponent>
    </>
  )
}

export default Dialog
